import React from 'react';

import BookIcon from '@atlaskit/icon/glyph/book';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import LightbulbIcon from '@atlaskit/icon/glyph/lightbulb';
import MediaServicesPreselectedIcon from '@atlaskit/icon/glyph/media-services/preselected';
import ShortcutIcon from '@atlaskit/icon/glyph/shortcut';
import Lozenge from '@atlaskit/lozenge';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { fontFallback } from '@atlaskit/theme/typography';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { FormattedMessage, FormattedNumber, useIntl } from '@atlassian/jira-intl';
import { useIsAdmin, useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller';
import { useTransitioningStatus } from '../../../services/store/transitioning-status';
import { Clouds } from './clouds';
import { messages } from './messages';
import { TodoItem } from './todo-item';

export const TransitionHomeRight = () => {
	const { formatMessage, formatNumber } = useIntl();
	const isSiteAdmin = useIsSiteAdmin();
	const isPrimaryAdmin = useIsAdmin();

	const [
		{
			isJnaAppDownloaded,
			areOgRulesDuplicated,
			areOgAppsDeactivated,
			jnaAppDownloadedRatio,
			jnaAppDownloadedUsersCount,
			ogAppExistsAtTransitioningStart,
		},
	] = useTransitioningStatus();

	const isOptimalTooling = ff('platform.show-optimal-tooling_brgom');

	return (
		<Box
			paddingBlockStart={isOptimalTooling ? 'space.600' : 'space.800'}
			paddingBlockEnd="space.300"
			paddingInline={isOptimalTooling ? 'space.500' : 'space.400'}
			xcss={rightSideWrapperBoxStyles}
		>
			{!isOptimalTooling && (
				<Stack space="space.400">
					<Stack space="space.150">
						<Box as="h2" xcss={titleHeadingBoxStyles}>
							<FormattedMessage {...messages.titleNonFinal} />
						</Box>
						<Inline space="space.100" alignBlock="center">
							{isJnaAppDownloaded ? (
								<CheckCircleIcon
									label={formatMessage(messages.doneNonFinal)}
									primaryColor={token('color.icon.subtle', '#626F86')}
								/>
							) : (
								<MediaServicesPreselectedIcon
									label={formatMessage(messages.todoNonFinal)}
									primaryColor={token('color.icon.subtle', '#626F86')}
								/>
							)}
							<FormattedMessage {...messages.appDownloadNonFinal} />
						</Inline>
						{ogAppExistsAtTransitioningStart && (
							<Inline space="space.100" alignBlock="center">
								{areOgRulesDuplicated ? (
									<CheckCircleIcon
										label={formatMessage(messages.doneNonFinal)}
										primaryColor={token('color.icon.subtle', '#626F86')}
									/>
								) : (
									<MediaServicesPreselectedIcon
										label={formatMessage(messages.todoNonFinal)}
										primaryColor={token('color.icon.subtle', '#626F86')}
									/>
								)}
								<FormattedMessage {...messages.jiraCloudContactMethodNonFinal} />
							</Inline>
						)}
						{ogAppExistsAtTransitioningStart && (
							<Inline space="space.100" alignBlock="center">
								{areOgAppsDeactivated ? (
									<CheckCircleIcon
										label={formatMessage(messages.doneNonFinal)}
										primaryColor={token('color.icon.subtle', '#626F86')}
									/>
								) : (
									<MediaServicesPreselectedIcon
										label={formatMessage(messages.todoNonFinal)}
										primaryColor={token('color.icon.subtle', '#626F86')}
									/>
								)}
								<FormattedMessage {...messages.turnOffOgNonFinal} />
							</Inline>
						)}
					</Stack>
					{(isPrimaryAdmin || isSiteAdmin) && (
						<Stack>
							<Box xcss={jnaAppDownloadPercentBoxStyles}>
								<FormattedNumber
									{...messages.jnaAppDownloadPercentNonFinal}
									value={jnaAppDownloadedRatio}
									// eslint-disable-next-line jira/react/no-style-attribute
									style="percent"
									maximumFractionDigits={0}
								/>
							</Box>
							<FormattedMessage
								{...messages.numberOfUsersJnaDownloadedNonFinal}
								values={{ numberOfUsers: jnaAppDownloadedUsersCount }}
							/>
						</Stack>
					)}
				</Stack>
			)}
			{isOptimalTooling && (
				<>
					<Stack space="space.400">
						<Stack space="space.150">
							<Box as="h2" xcss={titleHeadingBoxStyles}>
								<FormattedMessage {...messages.titleNonFinal} />
							</Box>
							<Stack space="space.075">
								<TodoItem
									description={formatMessage(messages.appDownloadNonFinal)}
									isChecked={isJnaAppDownloaded}
								/>
								{ogAppExistsAtTransitioningStart && (
									<TodoItem
										description={formatMessage(messages.jiraCloudContactMethodNonFinal)}
										isChecked={areOgRulesDuplicated}
									/>
								)}
								{ogAppExistsAtTransitioningStart && (
									<TodoItem
										description={formatMessage(messages.turnOffOgNonFinal)}
										isChecked={areOgAppsDeactivated}
									/>
								)}
								{jnaAppDownloadedUsersCount > 0 && (isPrimaryAdmin || isSiteAdmin) && (
									<Inline space="space.075" alignBlock="start">
										<LightbulbIcon
											label={formatMessage(messages.doneNonFinal)}
											primaryColor={token('color.link', colors.B400)}
										/>
										<Text color="color.text.subtlest">
											{formatMessage(messages.numberOfUsersJnaDownloadedWithLozengeNonFinal, {
												numberOfUsers: jnaAppDownloadedUsersCount,
												allUsers: formatNumber(jnaAppDownloadedRatio, {
													style: 'percent',
													maximumFractionDigits: 0,
												}),
												// eslint-disable-next-line @typescript-eslint/no-explicit-any
												lozenge: (str: any) => <Lozenge>{str}</Lozenge>,
											})}
										</Text>
									</Inline>
								)}
							</Stack>
						</Stack>

						<Box as="hr" xcss={dividerStyles} />

						<Stack space="space.150">
							<Box as="h2" xcss={titleHeadingBoxStyles}>
								{formatMessage(messages.documentationNonFinal)}
							</Box>
							<Box
								xcss={linkStyles}
								as="a"
								href="https://support.atlassian.com/jira-service-management-cloud/docs/set-up-operations-and-complete-your-shift-to-jira-service-management/"
								target="_blank"
								rel="noopener noreferrer"
							>
								<Box xcss={iconWrapperStyles}>
									<BookIcon label="" />
								</Box>
								{formatMessage(messages.unlockOperationsInJsmNonFinal)}
								<ShortcutIcon label="" size="small" />
							</Box>
						</Stack>
					</Stack>
					<Box xcss={cloudStyles}>
						<Clouds />
					</Box>
				</>
			)}
		</Box>
	);
};

const cloudStyles = xcss({
	width: '85px',
	position: 'absolute',
	bottom: 'space.0',
	right: 'space.0',
});

const rightSideWrapperBoxStyles = xcss({
	position: 'relative',
	width: '340px',
	height: '100%',
	flexShrink: 0,
	backgroundColor: 'color.background.accent.blue.subtlest',
	borderBottomRightRadius: 'border.radius',
	borderTopRightRadius: 'border.radius',
});

const iconWrapperStyles = xcss({
	display: 'flex',
	flexShrink: 0,
	height: '20px',
	width: '24px',
	justifyContent: 'center',
	alignItems: 'center',
});

const titleHeadingBoxStyles = xcss({
	color: 'color.text.subtlest',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.heading.xxsmall', fontFallback.heading.xxsmall),
	textTransform: 'uppercase',
});

const jnaAppDownloadPercentBoxStyles = xcss({
	color: 'color.text.accent.blue',
	fontWeight: 700,
});

const linkStyles = xcss({
	display: 'flex',
	columnGap: 'space.075',
	color: 'color.text.subtlest',
	':hover': {
		color: 'color.text.subtle',
	},
});

const dividerStyles = xcss({
	width: '100%',
	border: '1px solid',
	borderColor: 'color.border',
});
